import type {
  FileRouteContext,
  FileRouteSortBy,
  FileRouteViewMode,
} from '@jotta/types/schemas/FileRouteSchema'
import type { LinkProps, NavLinkProps } from 'react-router-dom'
import {
  EncodedRouteLink,
  EncodedRouteNavLink,
} from '@jotta/ui/EncodedRouterLink'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'
import { routeParamsToRoutePath } from '../FileRoute/utils/routeParamsToRoutePath'

export type FileRouteLinkOwnProps = {
  context?: FileRouteContext
  viewMode?: FileRouteViewMode
  sortBy?: FileRouteSortBy
  path?: string
  query?: string
}
export type FileRouteLinkProps<T extends NavLinkProps | LinkProps = LinkProps> =
  FileRouteLinkOwnProps & Omit<T, 'to'>
/**
 * Create a link from a partial Route object, preserves the current route context
 * (context, viewmode and sortby) unless you specifically override with your own values
 *
 * If you don't pass any props it will just link to the current route
 *
 * Extends the React Router Link component, and all of its props are valid except "to"
 *
 * @param props
 * @returns
 */
export function FileRouteLink(props: FileRouteLinkProps) {
  const { to, linkProps } = useFileRouteLinkProps('FileRouteLink', props)
  return <EncodedRouteLink to={to} {...linkProps} />
}

/**
 * Create a link from a partial Route object, preserves the current route context
 * (context, viewmode and sortby) unless you specifically override with your own values
 *
 * If you don't pass any props it will just link to the current route
 *
 * Extends the React Router NavLink component, and all of its props are valid except
 * "to"
 */
export function FileRouteNavLink(props: FileRouteLinkProps<NavLinkProps>) {
  const { to, linkProps } = useFileRouteLinkProps<NavLinkProps>(
    'FileRouteNavLink',
    props,
  )

  return <EncodedRouteNavLink to={to} {...linkProps} />
}

function useFileRouteLinkProps<T extends NavLinkProps | LinkProps = LinkProps>(
  name: string,
  {
    path: pathProp,
    context: contextProp,
    viewMode: viewModeProp,
    sortBy: sortByProp,
    query: queryProp,
    ...props
  }: FileRouteLinkProps<T>,
) {
  // const debug = Debug(`jotta:files:${name}`)
  const params = useFileRouteParams()
  const to = routeParamsToRoutePath({
    ['*']: pathProp || params['*'],
    context: contextProp || params.context,
    sortBy: sortByProp || params.sortBy,
    viewMode: viewModeProp || params.viewMode,
    query: queryProp || params.query,
  })
  return {
    to,
    linkProps: props,
  }
}
